import React, { useState, useEffect } from "react";
import axios from "../utils/axiosInstance"; // Using the correctly configured axiosInstance
import { useNavigate, useParams } from "react-router-dom"; 
import TopBanner from "./TopBanner"; // Assuming you have this component for layout
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress, 
  Alert,
  styled,
  IconButton,
  InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff } from '@mui/icons-material';

// Styled component for the submit button
const SubmitButton = styled(Button)({
  borderRadius: '12px',
  height: '48px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#a0a0a0',
  },
});

const SetInitialPassword = () => {
  // 1. Get userId and token from the URL parameters
  const { userId, token } = useParams(); 
  
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check for required URL parameters on load
  useEffect(() => {
    if (!userId || !token) {
      setErrorMessage("Error: Missing activation parameters. The link may be broken or invalid.");
    }
  }, [userId, token]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleTogglePassword = () => {
    setShowPassword(prev => !prev);
  };

  const validatePassword = () => {
    if (!password || password.length < 8) {
      setErrorMessage("Password must be at least 8 characters long.");
      return false;
    }
    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return false;
    }
    return true;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
  
    if (!validatePassword()) {
      return;
    }

    if (!userId || !token) {
        setErrorMessage("Setup failed. Missing security details.");
        return;
    }
  
    setIsSubmitting(true);
  
    try {
      // 2. Call the new backend endpoint
      const response = await axios.post(
        `/auth/set-initial-password`, 
        { 
          userId: userId,
          token: token,
          newPassword: password,
        },
        { 
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
  
      // --- ACTION ON SUCCESS ONLY ---
      setSuccessMessage(response.data?.msg || "Password set successfully! Redirecting to login...");
      setErrorMessage("");
      
      // Delay and navigate ONLY ON SUCCESS
      setTimeout(() => {
        navigate('/login'); 
      }, 3000);
      
      
    } catch (error) {
      // --- ACTION ON ERROR ONLY ---
      // This catch block will now correctly execute and stay on the page 
      // because the Axios interceptor has been updated to ignore this public route.
      const errMsg = error.response?.data?.msg || 
                     "Setup failed. The link may be expired or invalid. Please contact support.";
      setErrorMessage(errMsg);
      setSuccessMessage("");
      // Clear sensitive fields on error for security
      setPassword('');
      setConfirmPassword('');
      
    } finally {
      // Cleanup runs regardless of outcome.
      setIsSubmitting(false);
    }
  };


  return (
    <Box sx={{ 
      paddingTop: "64px", 
      minHeight: "calc(100vh - 64px)", 
      backgroundColor: "#f5f7fa",
      display: "flex",
      flexDirection: "column",
    }}>
      <TopBanner />
      
      <Box sx={{
        maxWidth: "480px",
        width: "100%",
        margin: "32px auto",
        padding: { xs: "24px", sm: "40px" },
        backgroundColor: "white",
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
      }}>
        <Typography 
          variant="h4" 
          sx={{ 
            textAlign: "center", 
            fontWeight: 700,
            color: "text.primary",
            mb: 1,
          }}
        >
          Set Your Password
        </Typography>
        
        <Typography 
          variant="body1" 
          sx={{ 
            textAlign: "center", 
            color: "text.secondary",
            mb: 4,
          }}
        >
          Create a new password to activate your account.
        </Typography>
        
        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}
        
        <Box 
          component="form" 
          onSubmit={handleFormSubmit}
        >
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="New Password (min. 8 characters)*"
            variant="outlined"
            value={password}
            onChange={handlePasswordChange}
            required
            sx={{ mb: 2 }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={handleTogglePassword}
                            edge="end"
                        >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
          />
          
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Confirm New Password *"
            variant="outlined"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            required
            sx={{ mb: 3 }}
          />
          
          <SubmitButton
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting || !userId || !token}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Activate & Set Password"
            )}
          </SubmitButton>
        </Box>
        
        {/* Optional: Link back to login */}
        <Box sx={{ textAlign: "center", pt: 3 }}>
            <Button
                onClick={() => navigate('/login')}
                sx={{ textTransform: "none", fontWeight: 600 }}
            >
                Back to Login
            </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SetInitialPassword;